import { useMemo } from 'react'
import {
  useGetProjectBugIndexQuery,
  useGetProjectPullRequestQuery as usePrDetail,
  useGetProjectTestSessionsInfiniteQuery as useSessionPages,
  useGetProjectTestSessionQuery as useSessionDetail,
} from '../../services/projectDataApi'
import { sessionBugData } from '../../lib/bug-management/testRunFindings'
import type { TestSession, PullRequest } from '../../data/projectTypes'

type FindingsQueryOptions = {
  skip?: boolean
  pollingInterval?: number
  skipPollingIfUnfocused?: boolean
  refetchOnMountOrArgChange?: boolean | number
  refetchCachedPages?: boolean
}

// Every consumer subscribes to the same project key. RTK Query owns one poll and one atomic
// replacement; session metadata and report pages never overwrite the authoritative findings.
export function useProjectFindings(projectId: string, skip = false, pollingInterval = 60_000) {
  return useGetProjectBugIndexQuery(projectId, {
    skip: skip || !projectId, pollingInterval, skipPollingIfUnfocused: true,
  })
}

export function useGetBugCountsQuery(projectId: string, options?: { skip?: boolean }) {
  const query = useProjectFindings(projectId, options?.skip)
  return { ...query, data: query.currentData?.counts, currentData: query.currentData?.counts }
}

function projectPrFindings<T extends PullRequest>(pr: T, index: NonNullable<ReturnType<typeof useProjectFindings>['currentData']>): T {
  const data = index.versions[pr.version_id]
  return { ...pr, bugs: data?.bugs ?? [], bug_count: data?.bug_summary.total ?? 0,
    new_bug_count: data?.findings.new_bugs.length ?? 0, rediscovery_count: data?.findings.rediscoveries.length ?? 0 }
}

const emptyFindings = sessionBugData({ bugs: [] })

// The index owns membership and status; only the detail read carries each bug's card fields.
// Degrade to the session's own server-composed findings when only the project-wide index fails
// (a transient 5xx, or a caller who can read the session but not the aggregate): an auxiliary
// counts read must not fail a page whose primary data arrived. The index stays authoritative
// whenever it resolves, including for sessions it reports as empty.
function detailFindings(
  session: TestSession,
  index: NonNullable<ReturnType<typeof useProjectFindings>['currentData']> | undefined,
  indexFailed: boolean,
) {
  const findings = index?.sessions[session.id]
  if (findings) {
    const cards = new Map(session.bugs.map(({ id, actual_behavior, defect_frames }) => [id, { actual_behavior, defect_frames }]))
    return sessionBugData({ bugs: findings.bug_associations.map(bug => ({ ...bug, ...cards.get(bug.id) })) })
  }
  return index || !indexFailed ? emptyFindings : sessionBugData(session)
}

export function useGetProjectTestSessionsInfiniteQuery(
  args: { projectId: string; pageSize?: number; includeMonitoring?: boolean }, options?: FindingsQueryOptions,
) {
  const query = useSessionPages(args, options)
  const index = useProjectFindings(args.projectId, options?.skip, options?.pollingInterval)
  const data = useMemo(() => query.currentData && index.currentData ? {
    ...query.currentData,
    pages: query.currentData.pages.map(page => ({
      ...page,
      githubPrs: page.githubPrs.map(pr => projectPrFindings(pr, index.currentData!)),
      items: page.items.map(session => ({ ...session, ...(index.currentData!.sessions[session.id] ?? emptyFindings) })),
    })),
  } : undefined, [query.currentData, index.currentData])
  return { ...query, data, currentData: data, isLoading: query.isLoading || (!options?.skip && !index.currentData && !index.isError),
    isError: query.isError || index.isError, error: query.error ?? index.error }
}

export function useGetProjectTestSessionQuery(
  args: { projectId: string; sessionId?: string; versionId?: string }, options?: FindingsQueryOptions,
) {
  const query = useSessionDetail(args, options)
  const index = useProjectFindings(args.projectId, options?.skip, options?.pollingInterval)
  const data = useMemo(() => {
    if (!query.currentData) return undefined
    const session: TestSession | null = query.currentData.session
    return { session: session ? { ...session, ...detailFindings(session, index.currentData, index.isError) } : null }
  }, [query.currentData, index.currentData, index.isError])
  return { ...query, data, currentData: data, isLoading: query.isLoading || (!options?.skip && !index.currentData && !index.isError),
    isError: query.isError, error: query.error }
}

export function useGetProjectPullRequestQuery(
  args: { projectId: string; versionId: string }, options?: FindingsQueryOptions,
) {
  const query = usePrDetail(args, options)
  const index = useProjectFindings(args.projectId, options?.skip, options?.pollingInterval)
  const data = useMemo(() => query.currentData && index.currentData
    ? { pr: projectPrFindings(query.currentData.pr, index.currentData) } : undefined,
  [query.currentData, index.currentData])
  return { ...query, data, currentData: data, isLoading: query.isLoading || (!options?.skip && !index.currentData && !index.isError),
    isError: query.isError || index.isError, error: query.error ?? index.error }
}

export function useGetBugBreakdownQuery(projectId: string, options?: { skip?: boolean }) {
  const query = useProjectFindings(projectId, options?.skip)
  return { ...query, data: query.currentData?.breakdown, currentData: query.currentData?.breakdown }
}
